<!-- TODO: Make CLOSE btn to always be visible.
-->
<!-- HISTORY: no-version
   V250509.1: Removed consumerCode from table + Changed association types' <v-autocomplete> to <v-select>.
   V250416.1: Added associationType + Added classes to <v-card> and <v-card-text> to keep the CLOSE button pinned to the bottom +
      Updated documentation accordingly and clarified how associationType is passed to the app.
   V250414.1: Made adjustments and improvements to the documentation.
   V250411.1: Added documentation for the Generic App 703.
   V250407.1: 1st version/release.
-->
<template>
<div class="px-0 py-0">
   <v-card flat class="px-0 py-0">
      <v-card-title class="pt-0 px-0">
         <span class="body-1 font-italic grey--text darken-4" style="color:#757575 !important">Generic Apps with UI: You can create up to 5 apps. Check out our documentation
            <span 
               @click="openDocDialog"
               class="ml-0 blue--text text--darken-2 text-decoration-underline"
               style="cursor: pointer;"
            >here</span>
         </span>
         <div class="flex-grow-1"></div>
         <v-btn small
            color="error"
            :disabled="items.length === 5"
            @click="newItemClicked()"
         >
            <v-icon left>add</v-icon>
            Add New App
         </v-btn>
      </v-card-title>

      <v-card-text class="px-0">
         <v-data-table dense fixed-header hide-default-footer
            class="elevation-1"
            item-key="name"
            :footer-props="{
               itemsPerPageOptions: [5],
               showFirstLastPage: false
            }"
            :headers="headers"
            :items="items"
            :items-per-page="5"
            :loading-text="$t('loading-text')"
            :no-data-text="$t('no-data-text', { value: 'items' })"
            :no-results-text="$t('no-results-text', { value: 'items' })"
         >
            <template v-slot:[`item.associationType`]="{ item }">
               {{ getAssociationTypes(item.associationType) }}
            </template>
            <template v-slot:[`item.action`]="{ item }">
               <v-icon small
                  @click="editItemClicked(item)"
               >edit</v-icon>
               <v-icon small
                  @click="deleteItemClicked(item)"
               >delete</v-icon>
            </template>
         </v-data-table>
      </v-card-text>
   </v-card>

   <v-dialog no-click-animation persistent
      max-width="1080px"
      v-model="docDialog"
   >
      <v-card flat class="d-flex flex-column" style="max-height: 90vh;">
         <v-card-title class="title grey--text darken-4 font-weight-bold pt-4 pb-2 pl-5">
            App UI Interaction with the Portal:
         </v-card-title>

         <v-card-text class="px-5 pb-0 flex-grow-1 overflow-y-auto">
            <!-- <v-container> -->
               <v-row>
                  <v-col cols="12" class="pb-0">
                     <p class="mb-4">
                        This documentation explains how to interact with our portal to send and receive <i>instruction</i> back and forth from within your HTML.
                        You (as the UI provider) are responsible for handling messages sent by the portal and sending results back.
                     </p>

                     <h3 class="subtitle-1 font-weight-bold mt-6">📨 Receiving <i>instruction</i> from Portal (Parent → HTML)</h3>
                     <!-- <p>
                        As soon as your HTMP page loads, the portal will send <code>{ instruction: {}, associationType: [] }</code> message using <code>postMessage</code>. You should listen for it
                        and act accordingly.
                     </p> -->
                     <p>
                        As soon as your HTMP page loads, the portal will send a message using <code>postMessage</code>. You should listen for it and act accordingly.
                     </p>

                     <v-card class="pa-3 my-3" outlined>
                        <pre><code>window.addEventListener("message", (event) =&gt; {
      console.log(JSON.stringify(event.data));
      // your code to process the instruction object goes here...
   });</code></pre>
                     </v-card>

                     <!-- <p>
                        The portal always sends the follwoing message:
                        <ul>
                           <li><code>{}</code> — for a new action (no instruction)</li>
                           <li><code>{ instruction: {} }</code> — for editing an existing action</li>
                        </ul>
                     </p> -->

                     <p>
                        The portal always sends the follwoing message: <code>{ instruction: {}, associationType: [] }</code>. <code>associationType</code> array contains one or more of the following values:
                        <ul>
                           <li>export</li>
                           <li>trigger</li>
                           <li>inbound</li>
                        </ul>
                     </p>

                     <h3 class="subtitle-1 font-weight-bold mt-6">📤 Sending <i>instruction</i> to Portal (HTML → Parent)</h3>
                     <p>
                        After the user completes the UI requirements, you must return a message back to the portal using <code>window.parent.postMessage</code>.
                     </p>

                     <h4 class="font-weight-bold mt-4 mb-2">🟢 On Save</h4>
                     <v-card class="pa-3 my-3" outlined>
                        <pre><code>const result = {
      instruction: {
         // all your key/value pairs go here
      },
      isCancelled: false
   };

   window.parent.postMessage(result, "*");</code></pre>
                     </v-card>

                     <h4 class="font-weight-bold mt-4 mb-2">🔴 On Cancel</h4>
                     <v-card class="pa-3 my-3" outlined>
                        <pre><code>const result = {
      isCancelled: true
   };

   window.parent.postMessage(result, "*");</code></pre>
                     </v-card>

                     <h3 class="subtitle-1 font-weight-bold mt-6">✅ Expected Flow</h3>
                     <ol class="mb-4">
                        <li>The portal opens your HTML.</li>
                        <li>Once loaded, your page receives a message from the portal containing:
                           <ul>
                              <!-- <li><code>{}</code> – for new entries</li>
                              <li><code>{ instruction: {} }</code> – for editing existing ones</li> -->
                              <li><code>{ instruction: {} }</code></li>
                           </ul>
                        </li>
                        <li>User completes the requirements in your page and clicks <strong>Save</strong> or <strong>Cancel</strong>.</li>
                        <li>Your page sends a message back with either:
                           <ul>
                              <li><code>{ instruction: {}, isCancelled: false }</code> – on Save</li>
                              <li><code>{ isCancelled: true }</code> – on Cancel</li>
                           </ul>
                        </li>
                        <li>The portal receives the message, closes your HTML page, and, if the instruction is provided, displays it in the Action's instruction text box. Please ensure that the instruction is a valid JSON object containing at least one key-value pair.</li>
                     </ol>

                     <p class="mt-6 grey--text text--darken-1">
                        💡 Tip: You may validate <code>event.origin</code> in your message handler to ensure you're receiving messages only from trusted sources.
                     </p>
                  </v-col>
               </v-row>
            <!-- </v-container> -->
         </v-card-text>

         <v-card-actions class="pt-0">
            <div class="flex-grow-1"></div>
            <v-btn text
               color="blue darken-1"
               @click="closeDocDialog"
            >Close</v-btn>
         </v-card-actions>
      </v-card>
   </v-dialog>

   <v-dialog no-click-animation persistent
      max-width="720px"
      v-model="mainDialog"
   >
      <v-form lazy-validation
         ref="mainForm"
         v-model="isMainFormValid"
      >
         <v-card flat>
            <v-card-title class="title grey--text darken-4 font-weight-bold pt-4 pb-2">Create a New Generic App:</v-card-title>
            <v-card-text class="px-5">
               <v-row>
                  <v-col cols="12" class="py-0">
                     <v-text-field autofocus persistent-hint required
                        ref="name"
                        autocomplete="off"
                        hint="* Name: 5 to 150 chars"
                        placeholder="enter a name for your generic app"
                        :rules="[rules.required, rules.nameLen, rules.duplicate]"
                        v-model="currItem.name"
                     ></v-text-field>
                  </v-col>
               </v-row>
               <v-row>
                  <v-col cols="6" class="py-0">
                     <v-select multiple persistent-hint required small-chips deletable-chips
                        ref="associationType"
                        autocomplete="off"
                        placeholder="select association type(s)..."
                        hint="* Association Types"
                        :items="associationTypeItems"
                        :rules="[rules.requiredArray]"
                        v-model="currItem.associationType"
                     ></v-select>
                  </v-col>
                  <v-col cols="6" class="py-0">
                     <v-text-field counter persistent-hint required
                        ref="consumerCode"
                        autocomplete="off"
                        hint="* Consumer Code"
                        placeholder="enter a consumer code with 32 to 36 chars"
                        :minlength="32"
                        :maxlength="36"
                        :rules="[rules.required, rules.consumerCodeLen]"
                        v-model="currItem.consumerCode"
                     ></v-text-field>
                  </v-col>
               </v-row>
               <v-row>
                  <v-col cols="12" class="py-0">
                     <v-text-field persistent-hint required
                        ref="appEndpoint"
                        autocomplete="off"
                        hint="* App Endpoint"
                        placeholder="enter a valid endpoint to POST the expoer/terigger actions to"
                        :rules="[rules.required, rules.endpoint]"
                        v-model="currItem.appEndpoint"
                     ></v-text-field>
                  </v-col>
               </v-row>
               <v-row>
                  <v-col cols="12" class="py-0">
                     <v-text-field persistent-hint required
                        ref="iframeUrl"
                        autocomplete="off"
                        hint="* iFrame URL"
                        placeholder="enter the iFrame URL for your UI"
                        :rules="[rules.required, rules.endpoint]"
                        v-model="currItem.iframeUrl"
                     ></v-text-field>
                  </v-col>
               </v-row>
            </v-card-text>

            <v-card-actions>
               <div class="flex-grow-1"></div>
               <v-btn text
                  color="blue darken-1"
                  @click="closeMainDialog"
               >Cancel</v-btn>
               <v-btn color="blue darken-1" text
                  :disabled="!isMainFormValid"
                  @click="saveItem"
               >Save</v-btn>
            </v-card-actions>
         </v-card>
      </v-form>
   </v-dialog>

   <v-dialog no-click-animation persistent scrollable
      max-width="560px"
      v-model="deleteDialog"
   >
      <v-card>
         <v-card-title class="title grey--text darken-4 font-weight-bold pt-3 pb-0">Deleting '{{currItem.name}}':</v-card-title>
         <v-card-text class="pb-0">
            <v-form ref="deleteForm" lazy-validation>
               <v-row class="pt-5 pb-0">
                  <v-col cols="12" class="pb-0">
                     <v-text-field autofocus outlined required
                        class="pb-0"
                        ref="currName"
                        label="* Name"
                        placeholder="enter the name of the desired Generic App to be deleted"
                        v-model="currName"
                     ></v-text-field>
                  </v-col>
               </v-row>
            </v-form>
         </v-card-text>

         <v-card-actions class="pt-0 pr-4">
            <div class="flex-grow-1"></div>
            <v-btn text
               color="blue darken-1"
               @click="closeDeleteDialog"
            >Cancel</v-btn>
            <v-btn text
               color="blue darken-1"
               :disabled="!currName || currName?.toLowerCase() != currItem.name?.toLowerCase()"
               @click="deleteItem"
            >Delete</v-btn>
         </v-card-actions>
      </v-card>
   </v-dialog>
</div>
</template>

<script>
import { toPascalCase } from '../mixins/bt-mixin.js';

const NAME = 'BtGenericAppSettings';
const ENDPOINT_PATTERN = /^(https?:\/\/)(www\.)?[\w.-]+\.[a-z]{2,}(:\d{1,5})?(\/[\w\-./?%&=]*)?$/;
const _trimObjectValues = obj => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
  );
};

export default {
   name: NAME,

   props: {
      value: {
         type: Array,
         required: true
      },      
      token: {
         type: String,
         required: true
      }
   },

   data() {
      return {
         items: [],
         rules: {
            required: value => (value && value.trim().length > 0) || "Value is required!",
            requiredArray: value => !!value.length || "Value is required!",
            nameLen: value => (value?.trim().length >= 5 && value?.trim().length <= 150) || "Value should have 5 to 150 chars!",
            consumerCodeLen: value => (value?.trim().length >= 32 && value?.trim().length <= 36) || "Value should have 32 to 36 chars!",
            endpoint: value => ENDPOINT_PATTERN.test(value?.trim()) || "Value is invalid!",
            duplicate: value => {
               const v = value?.trim()?.toLowerCase();
               if (v) {
                  const vItem = this.items.find(item => item.name.toLowerCase() === v);
                  if (vItem)
                     return this.isEdit && vItem.appCode === this.currItem.appCode || 'Value is duplicate!';
               }
               return true;
            }
         },
         headers: [
            { text: 'Name', value: 'name', align: 'left', sortable: true },
            // { text: 'Code', value: 'appCode', align: 'left', sortable: true },
            { text: 'Association Types', value: 'associationType', align: 'left', sortable: true },
            // { text: 'Consumer Code', value: 'consumerCode', align: 'left', sortable: true },
            { text: 'App Endpoint', value: 'appEndpoint', align: 'left', sortable: true },
            { text: 'iFrame URL', value: 'iframeUrl', align: 'left', sortable: true },
            { text: 'Actions', value: 'action', align: 'right', sortable: false }
         ],
         associationTypeItems: [
            { text: 'Export', value: 'export' },
            { text: 'Trigger', value: 'trigger' },
            { text: 'Inbound', value: 'inbound' }
         ],
         mainDialog: false,
         isMainFormValid: false,
         deleteDialog: false,
         currItem: {},
         currName: '',
         docDialog: false
      }
   },

   computed: { },

   watch: {
      token: {
         immediate: true,
         handler () {
            this.items = this.value?.length ? [...this.value] : [];
         }
      }
   },

   methods: {
      openDocDialog() {
         this.docDialog = true;
      },

      closeDocDialog() {
         this.docDialog = false;
      },

      getAssociationTypes(associationType) {
         return associationType?.map(t => toPascalCase(t))?.join(', ');
      },

      newItemClicked() {
         this.isEdit = false;
         this.currItem = {};
         // this.currItem = {
         //    name: 'app name',
         //    consumerCode: '0abcdefghi-1klmnoprs-2tuvwxyz-312',
         //    appEndpoint: 'https://mindfireinc.com/apps/app1',
         //    iframeUrl: 'https://mindfireinc.com/iframes/iframe1',
         //    appCode: 703-01
         // };
         this.mainDialog = true;
      },

      editItemClicked(item) {
         // alert(`in editItemClicked(): item=${JSON.stringify(item)}`);
         this.isEdit = true;
         this.currItem = JSON.parse(JSON.stringify(item));
         this.mainDialog = true;
      },

      saveItem() {
         if (!this.$refs.mainForm.validate()) return;

         const trimmedItem = _trimObjectValues(this.currItem);

         if (this.isEdit) {
            const ind = this.items.findIndex(item => item.appCode === trimmedItem.appCode);
            this.items.splice(ind, 1, trimmedItem);
         } else {
            trimmedItem.appCode = this.getNextAppCode();
            this.items.push(trimmedItem);
         }

         this.appSettingsChanged();
         this.closeMainDialog();
      },

      closeMainDialog() {
         this.mainDialog = false;
      },

      deleteItemClicked(item) {
         this.currItem = JSON.parse(JSON.stringify(item));
         this.currName = '';
         this.deleteDialog = true;
      },

      async deleteItem() {
         if (!this.$refs.deleteForm.validate()) return;

         const ind = this.items.findIndex(item => item.appCode === this.currItem.appCode);
         this.items.splice(ind, 1);
         this.appSettingsChanged();
         this.closeDeleteDialog();
      },

      closeDeleteDialog () {
         this.deleteDialog = false;
      },

      getNextAppCode() {
         if (this.items.length === 0) return "703-01";

         const max = Math.max(
            ...this.items.map(item => Number(item.appCode.split("-")[1]))
         );
         const next = (max + 1).toString().padStart(2, "0");
         return `703-${next}`;
      },

      appSettingsChanged() {
         this.$emit('input', this.items);
         this.$emit('change', this.items);
      }
   },

   created() { }
}
</script>

<style scoped>
.v-text-field input {
   padding: 4px 0 8px;
}
</style>